import { PORTRAIT_MAX_SIDE } from "../api/upload";

/**
 * ═══ CADRER SON VISAGE AVANT DE L'ENVOYER ═══
 *
 * Une photo prise au téléphone est un paysage : la personne y est en pied, de trois
 * quarts, avec la salle autour. Envoyée telle quelle, Rekognition n'y trouve qu'un
 * visage minuscule quand elle en trouve un, et l'avatar rond du site en montre le
 * torse. Le back office cadre depuis toujours (onglet « Visages », `react-easy-crop`) ;
 * ici on fait le même geste, avec la même sortie : un CARRÉ, centré sur le visage.
 *
 * ⚠️ LA GÉOMÉTRIE VIT ICI, PAS DANS LE COMPOSANT. Trois nombres suffisent à décrire ce
 * que la personne voit — la taille du carré à l'écran, le zoom, le décalage — et tout
 * le reste s'en déduit. Écrites dans les gestionnaires de pointeur, ces règles ne se
 * vérifient qu'à la main, sur un téléphone, avec une vraie photo ; écrites là, elles se
 * lisent et se testent (`crop.test.ts`).
 *
 * ⚠️ AUCUNE DÉPENDANCE AJOUTÉE. Le back office tire `react-easy-crop` ; ce paquet-ci
 * n'en a pas besoin : un carré, un glissement et un curseur de zoom tiennent en une
 * page, et le tunnel du participant est ce qu'on charge sur le wifi d'une salle.
 */

export type Size = { width: number; height: number };
export type Offset = { x: number; y: number };

/** Le carré découpé, en pixels de la photo d'origine. */
export type Rect = { x: number; y: number; size: number };

/**
 * Ce que la personne a sous les yeux : sa photo (`natural`, en pixels d'origine) dans un
 * carré de `view` pixels, agrandie de `zoom`, posée à `offset` du coin du carré.
 */
export type Frame = {
  natural: Size;
  /** Le côté du carré à l'écran, en pixels CSS. */
  view: number;
  /** 1 = la photo couvre tout juste le carré ; au-delà, on entre dedans. */
  zoom: number;
  /** Le coin haut-gauche de la photo, relatif à celui du carré. Négatif : elle déborde. */
  offset: Offset;
};

export const MIN_ZOOM = 1;
/**
 * Quatre fois, pas plus : au-delà, on cadre une bouillie de pixels sur une photo de
 * groupe, et ce n'est pas un visage que Rekognition y trouvera.
 */
export const MAX_ZOOM = 4;

/** La qualité du JPEG produit : au-dessus de `shrinkImage`, qui le réencodera. */
export const CROP_QUALITY = 0.92;

export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return MIN_ZOOM;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

/**
 * Le facteur qui fait que la photo COUVRE le carré — le plus grand des deux rapports.
 * Contenir plutôt que couvrir laisserait deux bandes vides dans le cadre, qui
 * partiraient blanches dans le portrait.
 */
export function coverScale(natural: Size, view: number): number {
  const width = Math.max(1, natural.width);
  const height = Math.max(1, natural.height);
  return Math.max(view / width, view / height);
}

/** La taille de la photo à l'écran, zoom compris. */
export function displayedSize(frame: Frame): Size {
  const scale = coverScale(frame.natural, frame.view) * clampZoom(frame.zoom);
  return {
    width: Math.max(1, frame.natural.width) * scale,
    height: Math.max(1, frame.natural.height) * scale,
  };
}

/**
 * ⚠️ JAMAIS DE BLANC DANS LE CADRE. Sans borne, on tire la photo hors du carré et le
 * portrait envoyé porte un coin vide — que la personne a pourtant vu se former, sans
 * qu'un seul écran le lui interdise. Le décalage est donc tenu entre « le bord droit de
 * la photo touche le bord droit du cadre » et « les bords gauches se touchent ».
 */
export function clampOffset(frame: Frame): Offset {
  const { width, height } = displayedSize(frame);
  const tenu = (position: number, taille: number): number => {
    /* Plus petite que le cadre (un arrondi près du zoom 1) : centrée, faute de mieux. */
    if (taille <= frame.view) return (frame.view - taille) / 2;
    return Math.min(0, Math.max(frame.view - taille, position));
  };
  return {
    x: tenu(frame.offset.x, width),
    y: tenu(frame.offset.y, height),
  };
}

/** La photo posée au milieu du cadre, ce qui est le cadrage d'avant tout geste. */
export function centeredOffset(natural: Size, view: number, zoom = MIN_ZOOM): Offset {
  const frame: Frame = { natural, view, zoom, offset: { x: 0, y: 0 } };
  const { width, height } = displayedSize(frame);
  return { x: (view - width) / 2, y: (view - height) / 2 };
}

/**
 * LE ZOOM NE DÉPLACE PAS CE QU'ON REGARDE.
 *
 * ⚠️ SANS ÇA, LE CURSEUR ÉLOIGNE DU VISAGE. Un zoom appliqué au seul facteur d'échelle
 * agrandit la photo depuis SON coin : le visage qu'on venait d'amener au centre part
 * vers le bas à droite, et il faut le rattraper après chaque cran. Ce qui est au centre
 * du cadre y reste.
 */
export function zoomKeepingCenter(frame: Frame, nextZoom: number): Offset {
  const before = coverScale(frame.natural, frame.view) * clampZoom(frame.zoom);
  const after = coverScale(frame.natural, frame.view) * clampZoom(nextZoom);
  const half = frame.view / 2;
  const point = {
    x: (half - frame.offset.x) / before,
    y: (half - frame.offset.y) / before,
  };
  return clampOffset({
    ...frame,
    zoom: nextZoom,
    offset: { x: half - point.x * after, y: half - point.y * after },
  });
}

/** Ce que le cadre montre, ramené en pixels de la photo d'origine. */
export function sourceRect(frame: Frame): Rect {
  const scale = coverScale(frame.natural, frame.view) * clampZoom(frame.zoom);
  const offset = clampOffset(frame);
  const size = Math.min(frame.view / scale, frame.natural.width, frame.natural.height);
  const tenu = (position: number, bord: number): number =>
    Math.min(Math.max(0, position), Math.max(0, bord - size));
  return {
    x: tenu(-offset.x / scale, frame.natural.width),
    y: tenu(-offset.y / scale, frame.natural.height),
    size,
  };
}

/**
 * ═══ LE GESTE, EN ARITHMÉTIQUE ═══
 *
 * Les doigts posés sur le cadre, et ce qu'on avait retenu quand le dernier s'est posé
 * (ou levé). Tout ce que `Cropper` fait d'un mouvement tient dans `applyGesture` : le
 * composant ne garde que les écouteurs.
 *
 * ⚠️ LE GESTE REPART À CHAQUE DOIGT POSÉ OU LEVÉ. Sans ce point de départ rafraîchi,
 * lever un doigt d'un pincement fait sauter la photo de tout l'écart accumulé : le
 * mouvement se recalculerait depuis le milieu de DEUX doigts, comparé à la position d'un
 * seul.
 */
export type GestureStart = {
  offset: Offset;
  /** Le milieu des doigts au départ du geste. */
  at: Offset;
  /** L'écartement des doigts au départ ; 0 s'il n'y en avait qu'un. */
  span: number;
  zoom: number;
};

/** Le milieu des doigts posés — c'est lui qui « tient » la photo. */
export function middleOf(points: ReadonlyArray<Offset>): Offset {
  if (points.length === 0) return { x: 0, y: 0 };
  const sum = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), {
    x: 0,
    y: 0,
  });
  return { x: sum.x / points.length, y: sum.y / points.length };
}

/** L'écartement de deux doigts. Zéro en dessous : il n'y a pas de pincement à un doigt. */
export function spanOf(points: ReadonlyArray<Offset>): number {
  const [a, b] = points;
  if (!a || !b) return 0;
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * CE QUE LE MOUVEMENT DONNE : un doigt déplace, deux doigts zooment.
 *
 * ⚠️ TOUT SE CALCULE DEPUIS LE DÉPART DU GESTE, jamais depuis le mouvement précédent.
 * Cumuler les petits écarts d'un `pointermove` à l'autre accumule les arrondis et, dès
 * qu'un mouvement est manqué (ils le sont, le navigateur en coalesce), la photo dérive
 * sous le doigt.
 */
export function applyGesture(
  frame: Frame,
  start: GestureStart,
  points: ReadonlyArray<Offset>,
): { zoom: number; offset: Offset } {
  const depart: Frame = { ...frame, zoom: start.zoom, offset: start.offset };
  const ecart = spanOf(points);

  if (points.length >= 2 && start.span > 0 && ecart > 0) {
    const zoom = clampZoom((start.zoom * ecart) / start.span);
    return { zoom, offset: zoomKeepingCenter(depart, zoom) };
  }

  const now = middleOf(points);
  return {
    zoom: frame.zoom,
    offset: clampOffset({
      ...depart,
      zoom: frame.zoom,
      offset: {
        x: start.offset.x + (now.x - start.at.x),
        y: start.offset.y + (now.y - start.at.y),
      },
    }),
  };
}

/**
 * Le côté du JPEG produit.
 *
 * ⚠️ ON NE GROSSIT JAMAIS. Un carré de 300 px rendu en 800 pèse trois fois plus sans
 * porter un détail de plus : ce qui manque à Rekognition ne s'invente pas au
 * redimensionnement. Le plafond, lui, est celui du portrait (`PORTRAIT_MAX_SIDE`) :
 * au-delà, `shrinkImage` réduirait juste après.
 */
export function cropSide(rect: Rect): number {
  return Math.max(1, Math.round(Math.min(PORTRAIT_MAX_SIDE, rect.size)));
}

/**
 * LE DÉCOUPAGE LUI-MÊME, dans le navigateur.
 *
 * ⚠️ C'EST L'IMAGE AFFICHÉE QUI EST DÉCOUPÉE, pas le fichier relu de son côté. Un
 * téléphone tenu droit écrit une image couchée et note la rotation dans ses métadonnées ;
 * le navigateur l'applique à l'élément `<img>`, donc les dimensions qu'on a mesurées et
 * ce que `drawImage` dessine parlent de la MÊME photo, celle que la personne cadre.
 * Relire le fichier ici ferait cadrer un portrait et envoyer un paysage.
 *
 * ⚠️ ET SI LE CANEVAS REFUSE, L'ORIGINAL PART. Le cadrage est un confort ; un navigateur
 * qui ne sait pas l'exécuter ne doit pas empêcher quelqu'un de poser sa photo.
 */
export async function cropToFile(
  image: CanvasImageSource,
  rect: Rect,
  fallback: File,
): Promise<File> {
  try {
    const side = cropSide(rect);
    const canvas = document.createElement("canvas");
    canvas.width = side;
    canvas.height = side;
    const paint = canvas.getContext("2d");
    if (!paint) return fallback;
    paint.imageSmoothingEnabled = true;
    paint.imageSmoothingQuality = "high";
    paint.drawImage(image, rect.x, rect.y, rect.size, rect.size, 0, 0, side, side);
    const blob = await new Promise<Blob | null>((done) =>
      canvas.toBlob(done, "image/jpeg", CROP_QUALITY),
    );
    if (!blob) return fallback;
    return new File([blob], "portrait.jpg", { type: "image/jpeg" });
  } catch {
    return fallback;
  }
}
